import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const Contacto = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    service: '',
    phone: '',
    privacy_consent: false
  });

  const servicios = [
    'CFO Externo',
    'Consultoría Financiera',
    'Centro de Servicios Contables',
    'Claridad Financiera',
    'Control y Rentabilidad',
    'Dirección Financiera Estratégica',
    'Orden Contable',
    'Gestión Administrativa Contable',
    'Backoffice Empresarial',
    'Crecimiento con Control',
    'Escalamiento Financiero',
    'Dirección y Expansión',
    'No estoy seguro - Necesito asesoría'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, privacy_consent: checked }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.company || !formData.email || !formData.service || !formData.phone) {
      setError('Por favor completa todos los campos obligatorios');
      return false;
    }
    if (!formData.privacy_consent) {
      setError('Debes aceptar la política de privacidad para continuar');
      return false;
    }
    setError('');
    return true;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1()) {
      return;
    }

    setLoading(true);
    
    try {
      // Enviar datos del paso 1 inmediatamente
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', 'da048cfc-ef90-459b-883c-cd2ff4b4f635');
      formDataToSend.append('subject', `Lead Calificado - ${formData.service}`);
      formDataToSend.append('from_name', 'Sinergia Empresarial - Contacto');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('botcheck', '');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitted(true);
        setError('');
      } else {
        setError('Error al enviar el formulario. Por favor intenta nuevamente.');
      }
    } catch (err) {
      setError('Error de conexión. Por favor verifica tu internet e intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        {/* Meta Tags Básicos */}
        <title>Contacto | Agenda tu Consulta Financiera | Sinergia Empresarial CSCE</title>
        <meta name="description" content="Contáctanos para agendar tu consulta inicial sin compromiso. Solicita un diagnóstico financiero gratuito para tu empresa en Colombia." />
        <meta name="keywords" content="contacto consultoría financiera, agendar consulta CFO, diagnóstico financiero gratuito, contacto contador público Colombia" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/contacto" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sinergiaempresarialcsce.com/contacto" />
        <meta property="og:title" content="Contacto | Agenda tu Consulta | Sinergia Empresarial CSCE" />
        <meta property="og:description" content="Agenda tu consulta inicial sin compromiso. Diagnóstico financiero gratuito para tu empresa." />
        <meta property="og:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.sinergiaempresarialcsce.com/contacto" />
        <meta name="twitter:title" content="Contacto | Sinergia Empresarial CSCE" />
        <meta name="twitter:description" content="Agenda tu consulta inicial sin compromiso." />
        <meta name="twitter:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "Sinergia Empresarial CSCE",
              "url": "https://www.sinergiaempresarialcsce.com",
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Customer Service",
                "areaServed": "CO",
                "availableLanguage": "Spanish"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="heading-1 text-5xl font-bold mb-4">
            Contacto
          </h1>
          <p className="body-large max-w-3xl">
            Agenda tu consulta y descubre cómo podemos ayudarte a alcanzar tus objetivos financieros.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="heading-2 text-3xl font-bold mb-6 text-sinergia-gris-oscuro">
                Hablemos de tu empresa
              </h2>
              <p className="body-medium text-gray-700 mb-8">
                Completa el formulario y nos pondremos en contacto contigo en horario laboral 
                (lunes a viernes, 8:00 AM - 5:00 PM) para agendar tu consulta inicial sin compromiso.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-sinergia-azul mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="heading-5 font-semibold mb-1 text-sinergia-gris-oscuro">Email</h3>
                    <p className="body-small text-gray-600">contacto@sinergiaempresarialcsce.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-6 h-6 text-sinergia-azul mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="heading-5 font-semibold mb-1 text-gray-900">Teléfono</h3>
                    <p className="body-small text-gray-600">+57 317 511 1422</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-sinergia-azul mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="heading-5 font-semibold mb-1 text-gray-900">Ubicación</h3>
                    <p className="body-small text-gray-600">Colombia</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-sinergia-gris-claro">
                <h3 className="heading-5 font-semibold mb-2 text-sinergia-gris-oscuro">
                  ¿Por qué elegirnos?
                </h3>
                <ul className="space-y-2 body-small text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sinergia-verde-claro mr-2 flex-shrink-0 mt-0.5" />
                    <span>Respuesta en menos de 24 horas hábiles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sinergia-verde-claro mr-2 flex-shrink-0 mt-0.5" />
                    <span>Consulta inicial sin compromiso</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sinergia-verde-claro mr-2 flex-shrink-0 mt-0.5" />
                    <span>Propuesta personalizada para tu empresa</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {submitted ? (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-sinergia-verde-claro mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="heading-5 font-semibold text-sinergia-gris-oscuro mb-2">
                        ¡Gracias por contactarnos!
                      </h3>
                      <p className="body-small text-gray-700">
                        Pronto uno de nuestros consultores se comunicará contigo.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleStep1Submit}>
                  <h3 className="heading-4 text-2xl font-bold mb-6 text-gray-900">
                    Información Básica
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nombre completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Tu nombre"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Empresa *</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Nombre de tu empresa"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Teléfono *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+57 XXX XXX XXXX"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email corporativo *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="tu@empresa.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service">Servicio de interés *</Label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleInputChange}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sinergia-azul"
                      >
                        <option value="">Selecciona un servicio</option>
                        {servicios.map((servicio, index) => (
                          <option key={index} value={servicio}>{servicio}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-start space-x-2 pt-2">
                      <Checkbox
                        id="privacy_consent"
                        checked={formData.privacy_consent}
                        onCheckedChange={handleCheckboxChange}
                        className="mt-1"
                      />
                      <Label htmlFor="privacy_consent" className="text-sm text-gray-600 cursor-pointer">
                        Acepto la{' '}
                        <Link to="/politica-privacidad" className="text-sinergia-azul hover:underline">
                          política de privacidad
                        </Link>
                        {' '}y el tratamiento de mis datos personales *
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-primary py-3 mt-4"
                    >
                      {loading ? 'Enviando...' : 'Enviar'}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
